"use client";

import { trans } from "@/service/i18n/i18n";
import { createPath } from "@/service/routing/routing";
import { MultiplePatrons } from "../../multiple-patrons/multiple-patrons";
import { Button } from "@/components/ui/button";
import { SuperLink as Link } from "@/components/common/super-link";
import { AwardIcon, ClipboardListIcon, DownloadIcon } from "lucide-react";
import { FinalCourseQuiz } from "./final-course-quiz";
import { useState } from "react";
import { RenewCourseButton } from "./renew-course-button";
import { cn } from "@/lib/utils";

export const OwnedCourseHeadingSection = ({
  course,
  patrons,
}: {
  course: MyCourseVM;
  patrons: PatronVM[];
}) => {
  const {
    title,
    subtitle,
    renew,
    hasAdditionalMaterials,
    slug,
    id,
    canStartFinalTest,
    bestScore,
    coverImage,
    testContentBlocks: testContentBlocksInvalidFormat,
    canDownloadCertificate,
  } = course;

  const [isTestActive, setIsTestActive] = useState(false);

  const testContentBlocks = testContentBlocksInvalidFormat.map((block) => ({
    ...block,
    data: {
      ...block.data,
      correct: block.data.options.flatMap(
        (opt: { correct: boolean }, i: number) => (opt.correct ? [i] : []),
      ),
    },
  }));

  const maxPatronsInRow = 3;

  return (
    <>
      <div
        className={cn(
          "@768:grid my-8 grid-cols-[2fr_1fr] gap-6",
          patrons.length > maxPatronsInRow && "@768:block",
        )}
      >
        <header className="flex flex-col gap-6">
          <h1 className="text-2xl font-bold text-zinc-950 md:text-3xl">
            {title}
          </h1>
          <p>{subtitle}</p>
        </header>
        <div className={cn("@768:hidden mt-4")}>
          <MultiplePatrons patrons={patrons} left />
        </div>
        <div
          className={cn(
            "@768:block hidden",
            patrons.length > maxPatronsInRow && "mt-4",
          )}
        >
          <div className="flex min-h-[36px] items-center">
            <MultiplePatrons patrons={patrons} />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-4">
        {hasAdditionalMaterials && (
          <Button asChild variant={"blue"}>
            <Link href={createPath("course.download", { slug, id })}>
              <DownloadIcon className="h-4 w-4" />
              {trans("education.course.download_additional_materials")}
            </Link>
          </Button>
        )}
        <span
          {...(canStartFinalTest
            ? {}
            : {
                "data-title": trans("education.course.can_start_final_test"),
              })}
          className="flex items-center gap-2"
        >
          <Button
            variant={"secondary"}
            disabled={!canStartFinalTest}
            onClick={() => setIsTestActive(true)}
          >
            <ClipboardListIcon className="h-4 w-4" />
            {trans("education.course.final_test")}
          </Button>
        </span>
        <span
          {...(canDownloadCertificate
            ? {}
            : {
                "data-title": trans(
                  "education.course.cannot_download_certificate",
                ),
              })}
        >
          <Button variant={"secondary"} disabled={!canDownloadCertificate}>
            <Link
              href={createPath("course.certificate", { slug, id })}
              className="flex items-center gap-2"
            >
              <AwardIcon className="h-4 w-4" />
              {trans("education.course.download_certificate")}
            </Link>
          </Button>
        </span>
        <RenewCourseButton course={course} />
      </div>
      <FinalCourseQuiz
        isTestActive={isTestActive}
        setIsTestActive={setIsTestActive}
        id={id}
        testContentBlocks={testContentBlocks}
        title={title}
      />
    </>
  );
};
